import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGroupSessions } from '@/hooks/useGroupSessions';
import { useAuth } from '@/contexts/AuthContext';
import { backendClient } from '@/lib/backendClient';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Save, UserPlus, X, Users, Crown } from 'lucide-react';
import { toast } from 'sonner';
import type { CreateGroupSessionRequest } from '@/types/groupSession';
import type { Participant } from '@/types/category';

const getGroupTypeIcon = (type: string) => {
  switch (type) {
    case 'family': return '👨‍👩‍👧‍👦';
    case 'yoga': return '🧘‍♀️';
    case 'organization': return '🏢';
    case 'team': return '👥';
    case 'friends': return '👫';
    default: return '📁';
  }
};

export default function CreateGroupPage() {
  const navigate = useNavigate();
  const { createGroupSession } = useGroupSessions();
  const { user } = useAuth();

  const [formData, setFormData] = useState<CreateGroupSessionRequest>({
    group_name: '',
    group_type: 'family',
    description: '',
    participants: [],
    organization_name: '',
    organization_biography: '',
    department: '',
  });

  const [ownerParticipant, setOwnerParticipant] = useState<Participant | null>(null);

  const [newParticipant, setNewParticipant] = useState({
    firstName: '',
    relationshipRole: ''
  });

  const [isCreating, setIsCreating] = useState(false);

  // Fetch and set owner as participant when component mounts
  useEffect(() => {
    const fetchOwnerInfo = async () => {
      if (!user) return;

      try {
        const { data: profile } = await backendClient
          .from('profiles')
          .select('first_name, preferred_name, full_name')
          .eq('id', user.id)
          .single();

        if (profile) {
          const ownerName = profile.preferred_name || profile.first_name || profile.full_name?.split(' ')[0] || 'Me';
          setOwnerParticipant({
            firstName: ownerName,
            consentToName: true,
            relationshipRole: 'Owner'
          });
        }
      } catch (error) {
        console.error('Failed to fetch owner info:', error);
        // Fallback to basic owner participant
        setOwnerParticipant({
          firstName: 'Me',
          consentToName: true,
          relationshipRole: 'Owner'
        });
      }
    };

    fetchOwnerInfo();
  }, [user]);

  const handleCreate = async () => {
    if (!formData.group_name.trim()) {
      toast.error('Group name is required');
      return;
    }

    if (formData.group_type === 'organization' && !formData.organization_name?.trim()) {
      toast.error('Organization name is required for organizational groups');
      return;
    }

    // Note: No need to validate for participants - the owner will be automatically added as a participant

    try {
      setIsCreating(true);
      const newGroup = await createGroupSession(formData);
      if (!newGroup) return; // Hook already showed an error toast
      toast.success('Group created successfully');
      navigate(`/groups/${newGroup.id}`);
    } catch (error) {
      toast.error('Failed to create group');
      console.error('Error creating group:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const addParticipant = () => {
    if (!newParticipant.firstName.trim()) {
      toast.error('Participant name is required');
      return;
    }

    const participant: Participant = {
      firstName: newParticipant.firstName.trim(),
      consentToName: true,
      relationshipRole: newParticipant.relationshipRole || undefined
    };

    setFormData(prev => ({
      ...prev,
      participants: [...prev.participants, participant]
    }));

    setNewParticipant({ firstName: '', relationshipRole: '' });
    toast.success('Participant added');
  };

  const removeParticipant = (index: number) => {
    setFormData(prev => ({
      ...prev,
      participants: prev.participants.filter((_, i) => i !== index)
    }));
    toast.success('Participant removed');
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/groups')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Groups
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{getGroupTypeIcon(formData.group_type)}</span>
            <h1 className="text-3xl font-bold tracking-tight">Create New Group</h1>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/groups')}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={isCreating}>
            <Save className="h-4 w-4 mr-2" />
            {isCreating ? 'Creating...' : 'Create Group'}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Group Information</CardTitle>
            <CardDescription>Basic details about your new group</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="group_name">Group Name *</Label>
              <Input
                id="group_name"
                value={formData.group_name}
                onChange={(e) => setFormData(prev => ({ ...prev, group_name: e.target.value }))}
                placeholder="Enter group name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="group_type">Group Type *</Label>
              <Select
                value={formData.group_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, group_type: value as any }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select group type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="family">👨‍👩‍👧‍👦 Family</SelectItem>
                  <SelectItem value="friends">👫 Friends</SelectItem>
                  <SelectItem value="team">👥 Team</SelectItem>
                  <SelectItem value="organization">🏢 Organization</SelectItem>
                  <SelectItem value="yoga">🧘‍♀️ Yoga</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your group (optional)"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Participants ({(ownerParticipant ? 1 : 0) + formData.participants.length})
            </CardTitle>
            <CardDescription>Group members (you're automatically included as owner)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3 p-3 border rounded-lg bg-muted/50">
              <div className="flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                <span className="font-medium text-sm">Add Participant</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Name *"
                  value={newParticipant.firstName}
                  onChange={(e) => setNewParticipant(prev => ({ ...prev, firstName: e.target.value }))}
                />
                <Input
                  placeholder="Role (optional)"
                  value={newParticipant.relationshipRole}
                  onChange={(e) => setNewParticipant(prev => ({ ...prev, relationshipRole: e.target.value }))}
                />
              </div>
              <Button onClick={addParticipant} size="sm" className="w-full">
                Add Participant
              </Button>
            </div>

            <div className="space-y-2">
              {/* Owner participant - always shown first */}
              {ownerParticipant && (
                <div className="flex items-center justify-between p-2 border rounded bg-primary/5">
                  <div className="flex items-center gap-2">
                    <Crown className="h-4 w-4 text-primary" />
                    <div>
                      <span className="font-medium">{ownerParticipant.firstName}</span>
                      <span className="text-muted-foreground text-sm ml-2">(Owner)</span>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">You</span>
                </div>
              )}

              {/* Additional participants */}
              {formData.participants.length > 0 ? (
                formData.participants.map((participant, index) => (
                  <div key={index} className="flex items-center justify-between p-2 border rounded">
                    <div>
                      <span className="font-medium">{participant.firstName}</span>
                      {participant.relationshipRole && <span className="text-muted-foreground text-sm ml-2">({participant.relationshipRole})</span>}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeParticipant(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-sm text-center py-4">
                  No additional participants yet. Add more members above.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {formData.group_type === 'organization' && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Organization Details</CardTitle>
              <CardDescription>Configure organizational context for personalized sessions</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="organization_name">Organization Name *</Label>
                  <Input
                    id="organization_name"
                    value={formData.organization_name || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, organization_name: e.target.value }))}
                    placeholder="e.g., TechCorp Inc, Wellness Center"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="department">Department/Team</Label>
                  <Input
                    id="department"
                    value={formData.department || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                    placeholder="e.g., Engineering, Marketing, HR"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="organization_biography">Organization Context</Label>
                <Textarea
                  id="organization_biography"
                  value={formData.organization_biography || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, organization_biography: e.target.value }))}
                  placeholder="Describe your organization's culture, values, current challenges, or specific context that should be woven into sessions..."
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  This context helps create more relevant and engaging meditation sessions for your team.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {formData.group_type === 'yoga' && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Yoga-Specific Settings</CardTitle>
              <CardDescription>Configure yoga session preferences (optional)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="opening_positions">Opening Positions</Label>
                  <Textarea
                    id="opening_positions"
                    value={formData.opening_positions || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, opening_positions: e.target.value }))}
                    placeholder="Starting yoga positions"
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="yoga_directives">Yoga Directives</Label>
                  <Textarea
                    id="yoga_directives"
                    value={formData.yoga_directives || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, yoga_directives: e.target.value }))}
                    placeholder="Special instructions or modifications"
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ending_positions">Ending Positions</Label>
                  <Textarea
                    id="ending_positions"
                    value={formData.ending_positions || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, ending_positions: e.target.value }))}
                    placeholder="Closing yoga positions"
                    rows={3}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="session_timing">Session Timing</Label>
                <Select
                  value={formData.session_timing || 'standalone'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, session_timing: value as any }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pre-yoga">Pre-Yoga (before practice)</SelectItem>
                    <SelectItem value="post-yoga">Post-Yoga (after practice)</SelectItem>
                    <SelectItem value="standalone">Standalone (independent)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
